import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Modal,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getAllColor } from "../../../User/State/Color/Action";
import { toast } from "react-toastify";
import { createColor, deleteColor, updateColor } from "../../State/ProductColor/Action";
import { findProductById } from "../../../User/State/Product/Action";

const ColorModal = ({ open, onClose, row, loading }) => {
  const { products, color, vendorProductColor } = useSelector((store) => store);
  const [editingId, setEditingId] = useState(null);
  const dispatch = useDispatch();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteColorId, setDeleteColorId] = useState(null);
  const [colors, setColors] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(getAllColor());
  }, [dispatch, row?.id]);

  useEffect(() => {
    dispatch(findProductById(row?.id));
    setColors(products?.product?.colors || []);
  }, [dispatch, products?.product, row?.id]);


  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedColors.length === 0) {
      toast.error("Please select  color !");
      return;
    }
    if (editingId) {
      dispatch(updateColor(editingId, selectedColors[0])).then(dispatch(findProductById(row?.id)));
    } else {
      dispatch(createColor({ productId: row?.id, colorIds: selectedColors })).then(dispatch(findProductById(row?.id)));
    }
    setSelectedColors([]);
    setEditingId(null);
  };

  const handleColorDelete = (colorId) => {
    setDeleteColorId(colorId);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    dispatch(deleteColor(deleteColorId)).then(dispatch(findProductById(row?.id)));
    setConfirmDialogOpen(false);
    setDeleteColorId(null);
    setSelectedColors([]);
    setEditingId(null);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
    setDeleteColorId(null);
  };


  const handleColorUpdate = (item) => {
    setSelectedColors(item?.id ? [item?.colorId] : []);
    setEditingId(item.id);
  };

  const handleModalClose = () => {
    setSelectedColors([]);
    setColors([])
    setEditingId(null);
    onClose(); // call parent close
  };


  const rows = colors?.map((color) => ({
    id: color?.id,
    colorName: color?.color?.name,
    colorId: color?.color?.id,
    productId: row?.id,
    product: row
  }))
    || [];

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      flex: 1,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "colorName", headerName: "Color Name", flex: 3 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 2,
      renderCell: (params) =>
        params?.row ? (
          <Box display="flex" gap={1} >
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => handleColorUpdate(params.row)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                onClick={() => handleColorDelete(params.row.id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : null,
    }
  ];

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "85%" : "70%",
            maxWidth: 800,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            maxHeight: "100vh",
            overflowY: "auto"
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Add Color</Typography>
            <IconButton onClick={handleModalClose}>
              <Close />
            </IconButton>
          </Box>

          {/* Readonly Field */}
          <TextField
            label="Product Title"
            value={row?.title}
            name="title"
            fullWidth
            disabled
            size="small"
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth size="small">
            <InputLabel id="color-label">Select Colors</InputLabel>
            <Select
              labelId="color-label"
              id="colorIds"
              name="colorIds"
              multiple
              value={selectedColors}
              onChange={(e) => setSelectedColors(e.target.value)}
              label="Select Colors"
            >
              <MenuItem value={0} disabled>
                Select Single Or Multiple color
              </MenuItem>
              {color.colors?.response?.map((color) => (
                <MenuItem key={color.id} value={color.id}>
                  {color.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>


          {/* Centered Button */}
          <Box display="flex" justifyContent="center" mb={2} mt={2}>
            <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "#ef4444", color: "#fff" }}>
              {editingId ? "Update Color" : "Add Color"}
            </Button>
          </Box>

          {/* Data Grid */}
          <Box height={280}>
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 5, page: 0 } },
              }}
              pageSizeOptions={[5, 10, 15]}
              disableRowSelectionOnClick
              density="compact"
              experimentalFeatures={{ virtualization: true }}
              loading={vendorProductColor?.loading || loading}
              autoHeight={true}
              sx={{
                borderRadius: 2,
                fontSize: "0.8rem",
                "& .MuiDataGrid-cell": {
                  fontSize: "0.8rem",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#449AEF",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.8rem",
                  padding: "8px",
                  border: "1px solid rgb(204, 201, 201)",
                  minHeight: "36px !important",
                  height: "36px !important",
                  lineHeight: "1.2",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontSize: "0.8rem",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-row": {
                  maxHeight: "36px !important",
                  minHeight: "36px !important",
                },
                "& .MuiDataGrid-row:nth-of-type(odd)": {
                  backgroundColor: "#f5f5f5",
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "#ffffff",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Box>
        </Box>

      </Modal>
      <Dialog open={confirmDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Color?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{
            "&:hover": {
              color: "white",
            },
          }} onClick={cancelDelete}>Cancel</Button>
          <Button sx={{
            "&:hover": {
              color: "white",
            },
          }} onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ColorModal;
